'use client';

import React, { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  Button,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useNotification } from '@/contexts/NotificationContext';

export type InventoryPurchasingSettings = {
  costingMethod: string;
  defaultReceiveLocationId?: number | null;
  buyingAlertsEnabled: boolean;
  alertRecipientUserIds: number[];
  alertRecipients: Array<{ id: number; email: string; firstName?: string; lastName?: string }>;
};

export type UserEmailOption = {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
};

type Props = {
  title?: string;
  fetchSettings: () => Promise<InventoryPurchasingSettings>;
  saveSettings: (payload: {
    costingMethod?: string;
    buyingAlertsEnabled?: boolean;
    alertRecipientUserIds?: number[];
  }) => Promise<InventoryPurchasingSettings>;
  searchUsers: (search: string) => Promise<UserEmailOption[]>;
  showCostingMethod?: boolean;
  costingMethod?: string;
  onCostingMethodChange?: (method: string) => void;
};

function userLabel(u: UserEmailOption): string {
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  return name ? `${u.email} (${name})` : u.email;
}

const InventoryAlertSettingsCard: React.FC<Props> = ({
  title = 'Inventory settings',
  fetchSettings,
  saveSettings,
  searchUsers,
  showCostingMethod = false,
  costingMethod: costingMethodProp,
  onCostingMethodChange,
}) => {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [buyingAlertsEnabled, setBuyingAlertsEnabled] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<UserEmailOption[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [userOptions, setUserOptions] = useState<UserEmailOption[]>([]);
  const [costingMethod, setCostingMethod] = useState('fifo');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await fetchSettings();
      setBuyingAlertsEnabled(s.buyingAlertsEnabled ?? false);
      setSelectedUsers(
        (s.alertRecipients ?? []).map((r) => ({
          id: r.id,
          email: r.email,
          firstName: r.firstName,
          lastName: r.lastName,
        })),
      );
      setCostingMethod(s.costingMethod ?? 'fifo');
      onCostingMethodChange?.(s.costingMethod ?? 'fifo');
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [fetchSettings, onCostingMethodChange, showError]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    const term = userSearch.trim();
    if (term.length < 1) {
      setUserOptions([]);
      return;
    }
    const t = setTimeout(() => {
      void searchUsers(term).then((list) => {
        if (!cancelled) setUserOptions(list.filter((u) => u.email?.trim()));
      });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [userSearch, searchUsers]);

  const effectiveCosting = costingMethodProp ?? costingMethod;

  const onSave = async () => {
    setSaving(true);
    try {
      await saveSettings({
        costingMethod: showCostingMethod ? effectiveCosting : undefined,
        buyingAlertsEnabled,
        alertRecipientUserIds: selectedUsers.map((u) => u.id),
      });
      showSuccess('Settings saved');
      await load();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, maxWidth: 520 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {title}
      </Typography>

      {showCostingMethod && (
        <TextField
          select
          size="small"
          fullWidth
          label="Costing method (outbound COGS)"
          value={effectiveCosting}
          onChange={(e) => {
            setCostingMethod(e.target.value);
            onCostingMethodChange?.(e.target.value);
          }}
          sx={{ mb: 2 }}
        >
          <MenuItem value="fifo">FIFO</MenuItem>
          <MenuItem value="lifo">LIFO</MenuItem>
        </TextField>
      )}

      <FormControlLabel
        control={
          <Switch
            checked={buyingAlertsEnabled}
            onChange={(_, v) => setBuyingAlertsEnabled(v)}
            disabled={loading}
          />
        }
        label="Send buying alert emails (nightly digest)"
      />

      <Autocomplete
        multiple
        size="small"
        sx={{ mt: 1 }}
        disabled={loading || !buyingAlertsEnabled}
        options={userOptions}
        value={selectedUsers}
        onChange={(_, v) => setSelectedUsers(v)}
        inputValue={userSearch}
        onInputChange={(_, v) => setUserSearch(v)}
        getOptionLabel={userLabel}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip {...getTagProps({ index })} key={option.id} label={option.email} size="small" />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Alert recipients"
            placeholder="Search users by email or name…"
            helperText={buyingAlertsEnabled ? 'Users must have an email on their account' : 'Enable alerts to add recipients'}
          />
        )}
      />

      <Button
        size="small"
        variant="contained"
        sx={{ mt: 2, textTransform: 'none' }}
        onClick={() => void onSave()}
        disabled={loading || saving}
      >
        Save settings
      </Button>
    </Paper>
  );
};

export default InventoryAlertSettingsCard;
